import { asRecord, readString } from '../../utils';

export type ForEachSuccessorOption = {
  readonly id: string;
  readonly label: string;
};

/**
 * Node types that may be a for-each loop body — the set the loop-body picker
 * offers ("agent, tool, workflow, or filter") and that `addForEachBodyNode` /
 * `adoptForEachBodyNode` accept. One source of truth so the editor's "move an
 * existing step inside" list and the "Configure" affordance can't drift from it.
 */
export const FOR_EACH_BODY_NODE_TYPES = [
  'agent',
  'tool-call',
  'filter',
  'run-workflow',
] as const;

/**
 * Body types with an in-place config form reachable from the for-each editor.
 * Everything in {@link FOR_EACH_BODY_NODE_TYPES} except `agent`, which opens its
 * own resource via the "Open" button instead. The body node is hidden from the
 * canvas, so this is the only route to its config.
 */
export const CONFIGURABLE_FOR_EACH_BODY_TYPES: readonly string[] =
  FOR_EACH_BODY_NODE_TYPES.filter((type) => type !== 'agent');

/** Whether a node type may be moved inside a for-each as its loop body. */
export function isForEachBodyType(nodeType: string | undefined): boolean {
  return (
    nodeType !== undefined &&
    (FOR_EACH_BODY_NODE_TYPES as readonly string[]).includes(nodeType)
  );
}

/** Whether a for-each body of this type can be configured in place from the editor. */
export function isConfigurableForEachBodyType(
  nodeType: string | undefined
): boolean {
  return (
    nodeType !== undefined &&
    CONFIGURABLE_FOR_EACH_BODY_TYPES.includes(nodeType)
  );
}

type GraphEdge = {
  readonly fromNodeId: string;
  readonly toNodeId: string;
  readonly createdAt?: string;
};

type GraphNode = {
  readonly id: string;
  readonly type: string;
  readonly data?: unknown;
};

/** Outgoing targets from a for-each, oldest edge first (matches draft child stamping). */
export function listForEachOutgoingIds(
  edges: readonly GraphEdge[],
  forEachNodeId: string
): string[] {
  return [...edges]
    .filter((edge) => edge.fromNodeId === forEachNodeId)
    .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''))
    .map((edge) => edge.toNodeId);
}

/**
 * Splits for-each successors into the loop body (fan-out) vs post-barrier
 * continuations (fan-in). Body is the explicit `childNodeId` when it is still
 * an outgoing target; otherwise there is no body (canvas outs are all fan-in).
 */
export function partitionForEachSuccessors(params: {
  outgoingIds: readonly string[];
  childNodeId: string | undefined;
}): { bodyId: string | undefined; fanInIds: readonly string[] } {
  const { outgoingIds, childNodeId } = params;
  if (
    childNodeId !== undefined &&
    childNodeId.trim() !== '' &&
    outgoingIds.includes(childNodeId)
  ) {
    return {
      bodyId: childNodeId,
      fanInIds: outgoingIds.filter((id) => id !== childNodeId),
    };
  }
  return { bodyId: undefined, fanInIds: outgoingIds };
}

/** Human-readable type badge for a loop-body / successor node. */
export function forEachBodyTypeLabel(nodeType: string): string {
  switch (nodeType) {
    case 'agent':
      return 'Agent';
    case 'tool-call':
      return 'Tool';
    case 'filter':
      return 'Filter';
    case 'for-each':
      return 'For each';
    default:
      return nodeType;
  }
}

/** Human-readable label for a successor option in the for-each editor. */
export function forEachSuccessorLabel(node: GraphNode): string {
  const data = asRecord(node.data);
  const name = readString(data, 'name');
  if (name !== undefined && name.trim() !== '') {
    return name;
  }
  switch (node.type) {
    case 'agent':
      return 'Agent';
    case 'tool-call': {
      const key = readString(data, 'key');
      return key !== undefined && key.trim() !== '' ? key : 'Tool call';
    }
    case 'filter':
      return 'Filter';
    case 'for-each':
      return 'For each';
    default:
      return node.type;
  }
}

export function listForEachSuccessorOptions(params: {
  nodes: readonly GraphNode[];
  edges: readonly GraphEdge[];
  forEachNodeId: string;
}): readonly ForEachSuccessorOption[] {
  const byId = new Map(params.nodes.map((node) => [node.id, node] as const));
  return listForEachOutgoingIds(params.edges, params.forEachNodeId).flatMap(
    (id) => {
      const node = byId.get(id);
      if (node === undefined) {
        return [];
      }
      return [{ id, label: forEachSuccessorLabel(node) }];
    }
  );
}

/** `fromNodeId:toNodeId` keys for for-each → post-barrier (fan-in) edges. */
export function listForEachFanInEdgeKeys(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[]
): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const node of nodes) {
    if (node.type !== 'for-each') {
      continue;
    }
    const childNodeId = readString(asRecord(node.data), 'childNodeId');
    const outgoingIds = listForEachOutgoingIds(edges, node.id);
    const { fanInIds } = partitionForEachSuccessors({
      outgoingIds,
      childNodeId,
    });
    for (const toId of fanInIds) {
      keys.add(`${node.id}:${toId}`);
    }
  }
  return keys;
}

export type ForEachBodySummary = {
  readonly id: string;
  readonly type: string;
  readonly typeLabel: string;
  readonly name: string;
};

/** Resolves the stamped loop-body node for canvas / editor display. */
export function resolveForEachBodySummary(params: {
  forEachNodeId: string;
  nodes: readonly GraphNode[];
  edges: readonly GraphEdge[];
}): ForEachBodySummary | null {
  const forEach = params.nodes.find((node) => node.id === params.forEachNodeId);
  if (forEach === undefined || forEach.type !== 'for-each') {
    return null;
  }
  const childNodeId = readString(asRecord(forEach.data), 'childNodeId');
  const outgoingIds = listForEachOutgoingIds(
    params.edges,
    params.forEachNodeId
  );
  const { bodyId } = partitionForEachSuccessors({
    outgoingIds,
    childNodeId,
  });
  if (bodyId === undefined) {
    return null;
  }
  const body = params.nodes.find((node) => node.id === bodyId);
  if (body === undefined) {
    return null;
  }
  return {
    id: body.id,
    type: body.type,
    typeLabel: forEachBodyTypeLabel(body.type),
    name: forEachSuccessorLabel(body),
  };
}

/**
 * Stamps `bodyType` / `bodyName` on for-each nodes from the live body node.
 * Save only persists `childNodeId`; display fields are derived for the badge.
 */
export function withForEachBodyDisplayFields<
  TGraph extends { nodes: readonly GraphNode[]; edges: readonly GraphEdge[] }
>(graph: TGraph): TGraph {
  let changed = false;
  const nodes = graph.nodes.map((node) => {
    if (node.type !== 'for-each') {
      return node;
    }
    const summary = resolveForEachBodySummary({
      forEachNodeId: node.id,
      nodes: graph.nodes,
      edges: graph.edges,
    });
    const data = { ...asRecord(node.data) };
    const prevType = readString(data, 'bodyType');
    const prevName = readString(data, 'bodyName');
    if (summary === null) {
      if (prevType === undefined && prevName === undefined) {
        return node;
      }
      changed = true;
      delete data['bodyType'];
      delete data['bodyName'];
      return { ...node, data };
    }
    if (prevType === summary.type && prevName === summary.name) {
      return node;
    }
    changed = true;
    data['bodyType'] = summary.type;
    data['bodyName'] = summary.name;
    return { ...node, data };
  });

  if (!changed) {
    return graph;
  }
  return { ...graph, nodes };
}

/**
 * The hidden fan-out interior, each node mapped to the for-each whose loop body
 * it sits in.
 *
 * The walk that finds the interior already knows which loop it descended from,
 * so recording it costs nothing and answers the question a bare set cannot:
 * *which* for-each a hidden step belongs to. Owners here are the immediate
 * for-each, which may itself be hidden — see
 * {@link resolveForEachInteriorOwners}.
 */
function walkForEachInteriors(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[]
): ReadonlyMap<string, string> {
  const fanInTargets = new Set<string>();
  /** Loop bodies, paired with the for-each that runs each one. */
  const seeds: { readonly bodyId: string; readonly forEachId: string }[] = [];
  const forEachIds = new Set<string>();

  for (const node of nodes) {
    if (node.type !== 'for-each') {
      continue;
    }
    forEachIds.add(node.id);
    const childNodeId = readString(asRecord(node.data), 'childNodeId');
    const outgoingIds = listForEachOutgoingIds(edges, node.id);
    const { bodyId, fanInIds } = partitionForEachSuccessors({
      outgoingIds,
      childNodeId,
    });
    for (const id of fanInIds) {
      fanInTargets.add(id);
    }
    if (bodyId !== undefined) {
      seeds.push({ bodyId, forEachId: node.id });
    }
  }

  const ownerById = new Map<string, string>();
  const queue: string[] = [];
  for (const { bodyId, forEachId } of seeds) {
    if (ownerById.has(bodyId)) {
      continue;
    }
    ownerById.set(bodyId, forEachId);
    queue.push(bodyId);
  }

  while (queue.length > 0) {
    const id = queue.pop();
    if (id === undefined) {
      break;
    }
    const owner = ownerById.get(id);
    if (owner === undefined) {
      continue;
    }
    for (const edge of edges) {
      if (edge.fromNodeId !== id) {
        continue;
      }
      const next = edge.toNodeId;
      if (
        ownerById.has(next) ||
        fanInTargets.has(next) ||
        forEachIds.has(next)
      ) {
        continue;
      }
      // Everything downstream of a body runs inside the same loop, so it belongs
      // to the same for-each.
      ownerById.set(next, owner);
      queue.push(next);
    }
  }

  return ownerById;
}

/**
 * Every hidden interior node mapped to the for-each card the canvas actually
 * draws for it.
 *
 * Which is not always its immediate for-each: a for-each nested directly inside
 * another loop's body is itself hidden, so its interior has to report the
 * outermost visible loop instead. Anything whose chain cannot be resolved to a
 * drawn node is left out rather than pointing at a card that is not there.
 *
 * Callers use this to attach something to a step the canvas does not draw — the
 * hierarchy view hangs a nested workflow's "runs" edge off the for-each that
 * runs it.
 */
export function resolveForEachInteriorOwners(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[]
): ReadonlyMap<string, string> {
  const ownerById = walkForEachInteriors(nodes, edges);
  const visibleOwners = new Map<string, string>();
  for (const [nodeId, immediateOwner] of ownerById) {
    let owner = immediateOwner;
    const seen = new Set<string>([nodeId]);
    // Hop up through hidden for-eaches. `ownerById` only ever points at a node
    // nearer the top of a loop, so this terminates; the seen-set is belt and
    // braces against a graph that somehow made it circular.
    while (ownerById.has(owner) && !seen.has(owner)) {
      seen.add(owner);
      owner = ownerById.get(owner) ?? owner;
    }
    if (!ownerById.has(owner)) {
      visibleOwners.set(nodeId, owner);
    }
  }
  return visibleOwners;
}

/**
 * Node ids that belong to the hidden fan-out interior: the stamped loop body
 * and anything reachable from it that is not a for-each fan-in target.
 */
export function listHiddenForEachInteriorNodeIds(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[]
): ReadonlySet<string> {
  return new Set(walkForEachInteriors(nodes, edges).keys());
}
